export const filterTemplatesByChannel = (templates, channelId) => {
  if (!channelId || channelId === 'all') return templates;

  return templates.filter(
    template => Number(template.channel_id) === Number(channelId)
  );
};

export const TEMPLATE_NAME_PATTERN = /^[a-z0-9_]+$/;
export const MAX_TEMPLATE_NAME_LENGTH = 512;
export const MAX_TEMPLATE_BUTTONS = 10;
export const PARAMETER_FORMATS = {
  POSITIONAL: 'POSITIONAL',
  NAMED: 'NAMED',
};

const VARIABLE_PATTERN = /\{\{([^{}]+)\}\}/g;
const POSITIONAL_VARIABLE_PATTERN = /^\d+$/;
const NAMED_VARIABLE_PATTERN = /^[a-z][a-z_]*$/;

const alphabeticSuffix = value => {
  let number = value;
  let suffix = '';

  while (number > 0) {
    number -= 1;
    suffix = String.fromCharCode(97 + (number % 26)) + suffix;
    number = Math.floor(number / 26);
  }

  return suffix;
};

const generatedVariableName = index => `variable_${alphabeticSuffix(index)}`;

export const MEDIA_RULES = {
  image: {
    accept: 'image/jpeg,image/png',
    contentTypes: ['image/jpeg', 'image/png'],
    extensions: ['jpg', 'jpeg', 'png'],
    maxBytes: 5 * 1024 * 1024,
  },
  video: {
    accept: 'video/mp4,video/3gpp',
    contentTypes: ['video/mp4', 'video/3gpp'],
    extensions: ['mp4', '3gp'],
    maxBytes: 16 * 1024 * 1024,
  },
  document: {
    accept:
      '.pdf,.txt,.doc,.docx,.xls,.xlsx,.ppt,.pptx,application/pdf,text/plain',
    contentTypes: [
      'text/plain',
      'application/pdf',
      'application/msword',
      'application/vnd.ms-excel',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ],
    extensions: ['pdf', 'txt', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'],
    maxBytes: 100 * 1024 * 1024,
  },
};

export const templateNameError = name => {
  if (!name) return 'required';
  if (name.length > MAX_TEMPLATE_NAME_LENGTH) return 'length';
  if (!TEMPLATE_NAME_PATTERN.test(name)) return 'format';
  return '';
};

export const mediaFileError = (file, templateKind) => {
  if (!file || !MEDIA_RULES[templateKind]) return '';

  const rules = MEDIA_RULES[templateKind];
  const extension = file.name?.split('.').pop()?.toLowerCase();
  if (!rules.extensions.includes(extension)) return 'type';
  const contentType = file.type?.toLowerCase();
  if (
    contentType &&
    contentType !== 'application/octet-stream' &&
    !rules.contentTypes.includes(contentType)
  ) {
    return 'type';
  }
  if (!file.size) return 'empty';
  if (file.size > rules.maxBytes) return 'size';
  return '';
};

export const variableIdentifiers = (text, parameterFormat) => {
  const identifiers = [...text.matchAll(VARIABLE_PATTERN)].map(
    match => match[1]
  );
  const validPattern =
    parameterFormat === PARAMETER_FORMATS.NAMED
      ? NAMED_VARIABLE_PATTERN
      : POSITIONAL_VARIABLE_PATTERN;
  const uniqueIdentifiers = [
    ...new Set(identifiers.filter(identifier => validPattern.test(identifier))),
  ];

  return parameterFormat === PARAMETER_FORMATS.NAMED
    ? uniqueIdentifiers
    : uniqueIdentifiers.map(Number).sort((a, b) => a - b);
};

export const hasInvalidVariables = (text, parameterFormat) => {
  const identifiers = [...text.matchAll(VARIABLE_PATTERN)].map(
    match => match[1]
  );
  const textWithoutVariables = text.replace(VARIABLE_PATTERN, '');
  const validPattern =
    parameterFormat === PARAMETER_FORMATS.NAMED
      ? NAMED_VARIABLE_PATTERN
      : POSITIONAL_VARIABLE_PATTERN;

  return (
    textWithoutVariables.includes('{{') ||
    textWithoutVariables.includes('}}') ||
    identifiers.some(identifier => !validPattern.test(identifier))
  );
};

export const convertVariableFormat = (text, parameterFormat) => {
  const replacements = new Map();

  return text.replace(VARIABLE_PATTERN, (_match, identifier) => {
    if (!replacements.has(identifier)) {
      const index = replacements.size + 1;
      replacements.set(
        identifier,
        parameterFormat === PARAMETER_FORMATS.NAMED
          ? generatedVariableName(index)
          : String(index)
      );
    }

    return `{{${replacements.get(identifier)}}}`;
  });
};

export const nextBodyVariable = (
  body,
  parameterFormat = PARAMETER_FORMATS.POSITIONAL
) => {
  const identifiers = variableIdentifiers(body, parameterFormat);
  if (parameterFormat === PARAMETER_FORMATS.NAMED) {
    let index = 1;
    while (identifiers.includes(generatedVariableName(index))) index += 1;
    return `{{${generatedVariableName(index)}}}`;
  }

  return `{{${identifiers.length ? Math.max(...identifiers) + 1 : 1}}}`;
};

export const insertAtSelection = ({
  value,
  selectionStart,
  selectionEnd,
  prefix,
  suffix = '',
  placeholder = '',
}) => {
  const selectedText = value.slice(selectionStart, selectionEnd);
  const content = selectedText || placeholder;
  const insertedText = `${prefix}${content}${suffix}`;
  const updatedValue =
    value.slice(0, selectionStart) + insertedText + value.slice(selectionEnd);
  const contentStart = selectionStart + prefix.length;

  return {
    value: updatedValue,
    selectionStart: contentStart,
    selectionEnd: contentStart + content.length,
  };
};

export const renderWhatsappFormatting = value => {
  const escaped = value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');

  return escaped
    .replace(/```([^`]+)```/g, '<code>$1</code>')
    .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
    .replace(/_([^_\n]+)_/g, '<em>$1</em>')
    .replace(/~([^~\n]+)~/g, '<s>$1</s>');
};

export const buildTemplatePreview = configuration => {
  const normalizedConfiguration = configuration || {};
  const body = normalizedConfiguration.body || '';
  const header = normalizedConfiguration.header_text || '';
  const parameterFormat =
    normalizedConfiguration.parameter_format ||
    (variableIdentifiers(`${header} ${body}`, PARAMETER_FORMATS.NAMED).length
      ? PARAMETER_FORMATS.NAMED
      : PARAMETER_FORMATS.POSITIONAL);
  const bodyVariables = variableIdentifiers(body, parameterFormat);
  const headerVariables = variableIdentifiers(header, parameterFormat);

  const previewBody = bodyVariables.reduce((value, variable, index) => {
    const example =
      normalizedConfiguration.body_examples?.[index] || `{{${variable}}}`;
    return value.replaceAll(`{{${variable}}}`, example);
  }, body);
  const [headerVariable] = headerVariables;
  const previewHeader = headerVariable
    ? header.replaceAll(
        `{{${headerVariable}}}`,
        normalizedConfiguration.header_example || `{{${headerVariable}}}`
      )
    : header;

  return {
    templateType: normalizedConfiguration.template_type || 'default',
    header: previewHeader,
    body: previewBody,
    footer: normalizedConfiguration.footer || '',
    buttons: normalizedConfiguration.buttons || [],
    flowCta: normalizedConfiguration.flow_cta || '',
  };
};
